import React, { useMemo } from 'react';
import { MessageSquareQuote } from 'lucide-react';
import { formatAiReasonForDisplay } from '../../utils/helper';
import { cn } from '../ui/utils';

const toneStyles = {
  CORRECT: {
    container: 'border-emerald-200/80 bg-gradient-to-br from-emerald-50 via-white to-[#C0E281]/15',
    icon: 'border-emerald-200 bg-emerald-100 text-emerald-700',
    badge: 'bg-emerald-100 text-emerald-800',
    title: 'text-emerald-900',
    body: 'text-emerald-900/80'
  },
  PARTIAL: {
    container: 'border-amber-200/80 bg-gradient-to-br from-amber-50 via-white to-orange-50',
    icon: 'border-amber-200 bg-amber-100 text-amber-700',
    badge: 'bg-amber-100 text-amber-800',
    title: 'text-amber-900',
    body: 'text-amber-900/80'
  },
  INCORRECT: {
    container: 'border-rose-200/80 bg-gradient-to-br from-rose-50 via-white to-orange-50',
    icon: 'border-rose-200 bg-rose-100 text-rose-700',
    badge: 'bg-rose-100 text-rose-800',
    title: 'text-rose-900',
    body: 'text-rose-900/80'
  },
  SKIPPED: {
    container: 'border-border bg-muted/40',
    icon: 'border-border bg-muted text-muted-foreground',
    badge: 'bg-muted text-muted-foreground',
    title: 'text-foreground',
    body: 'text-muted-foreground'
  }
};

/**
 * Highlighted AI evaluation reason on the review page.
 * Sanitizes legacy/broken reason strings that include HTML or "[object Object]".
 */
const AiReasonBanner = ({ reason, answerStatus, score, maxScore, className }) => {
  const displayReason = useMemo(() => formatAiReasonForDisplay(reason), [reason]);
  const hasScore = score != null || maxScore != null;
  if (!displayReason && !hasScore) return null;

  const styles = toneStyles[answerStatus] || toneStyles.SKIPPED;

  return (
    <div
      role="note"
      className={cn(
        'w-full overflow-hidden rounded-2xl border p-4 shadow-sm sm:p-5',
        styles.container,
        className
      )}
    >
      <div className="flex items-start gap-3.5">
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border',
            styles.icon
          )}
        >
          <MessageSquareQuote className="h-5 w-5" aria-hidden />
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex flex-wrap items-center gap-2">
            <span
              className={cn(
                'inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
                styles.badge
              )}
            >
              {displayReason ? 'Evaluation reason' : 'Score'}
            </span>
            {hasScore && (
              <span
                className={cn(
                  'inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold tabular-nums',
                  styles.badge
                )}
              >
                {score ?? 0} / {maxScore ?? 0} points
              </span>
            )}
          </div>
          {displayReason ? (
            <p className={cn('text-sm leading-relaxed whitespace-pre-wrap', styles.body)}>
              {displayReason}
            </p>
          ) : (
            <p className={cn('text-sm', styles.body)}>No evaluation reason was provided.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiReasonBanner;
